/**
 * src/app/auth.ts
 * FlareDrive-Pro 客户端认证与用户状态管理模块
 * 
 * 职责：
 * 1. 管理 Token (JWT/Admin Token) 与 Basic Auth 凭据的持久化与读取
 * 2. 提供全局统一的 HTTP 鉴权请求头注入 (getAuthHeaders)
 * 3. 维护当前登录用户身份及存储配额信息 (CurrentUser, Quota)
 * 4. 提供与后端 /api/center/* 和 /api/me 的接口通信能力
 * 5. 全局认证状态事件监听与广播
 */

export type UserRole = "admin" | "user";
export type UserPermission = "rw" | "ro";

export interface CurrentUser {
  id?: string;
  username: string;
  role: UserRole;
  enabled: boolean;
  rootPath: string;
  permission: UserPermission;
  quotaBytes: number;
  usedBytes: number;
  createdAt?: number;
  updatedAt?: number;
}

const STORAGE_KEY_TOKEN = "flaredrive_token";
const STORAGE_KEY_BASIC_AUTH = "flaredrive_basic_auth";
const STORAGE_KEY_USER = "flaredrive_user";

// 内存中缓存的当前用户信息
let inMemoryUser: CurrentUser | null = null;

// 认证状态监听器列表
type AuthStateListener = (user: CurrentUser | null) => void;
const listeners = new Set<AuthStateListener>();

/**
 * 订阅认证状态变化
 */
export function onAuthStateChange(listener: AuthStateListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * 广播认证状态变更
 */
function broadcastAuthState(user: CurrentUser | null) {
  inMemoryUser = user;
  listeners.forEach((listener) => {
    try {
      listener(user);
    } catch (e) {
      console.error("Auth state listener error:", e);
    }
  });
}

/**
 * 获取本地存储的 Token
 */
export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_TOKEN);
  } catch {
    return null;
  }
}

/**
 * 获取本地存储的 Basic Auth Header (例如 "Basic YWRtaW46YWRtaW4xMjM=")
 */
export function getStoredBasicAuth(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_BASIC_AUTH);
  } catch {
    return null;
  }
}

/**
 * 获取当前已登录用户信息
 */
export function getCurrentUser(): CurrentUser | null {
  if (inMemoryUser) return inMemoryUser;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_USER);
    if (raw) {
      inMemoryUser = JSON.parse(raw);
      return inMemoryUser;
    }
  } catch (e) {
    console.warn("Failed to parse stored user from localStorage", e);
  }
  return null;
}

/**
 * 生成或获取通用的请求鉴权头
 * @param forAdmin 是否用于管理接口（若有 Token 则优先使用 Bearer Token，同时附加 x-admin-token）
 */
export function getAuthHeaders(options?: { forAdmin?: boolean }): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getStoredToken();
  const basicAuth = getStoredBasicAuth();

  if (options?.forAdmin) {
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
      headers["x-admin-token"] = token;
    } else if (basicAuth) {
      headers["Authorization"] = basicAuth;
    }
  } else {
    // 普通 WebDAV 或数据接口
    if (basicAuth) {
      headers["Authorization"] = basicAuth;
    } else if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  return headers;
}

/**
 * 解析 Basic Auth 中的用户名
 */
export function parseUsernameFromBasicAuth(basicAuth: string): string | null {
  try {
    if (!basicAuth.startsWith("Basic ")) return null;
    const base64Str = basicAuth.slice(6).trim();
    const decoded = decodeURIComponent(escape(atob(base64Str)));
    const colonIndex = decoded.indexOf(":");
    if (colonIndex > 0) {
      return decoded.slice(0, colonIndex);
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * 保存登录凭据与用户信息
 */
export function saveAuthCredentials(data: {
  token?: string | null;
  basicAuth?: string | null;
  user?: CurrentUser | null;
}) {
  try {
    if (data.token) {
      localStorage.setItem(STORAGE_KEY_TOKEN, data.token);
    } else if (data.token === null) {
      localStorage.removeItem(STORAGE_KEY_TOKEN);
    }

    if (data.basicAuth) {
      localStorage.setItem(STORAGE_KEY_BASIC_AUTH, data.basicAuth);
    } else if (data.basicAuth === null) {
      localStorage.removeItem(STORAGE_KEY_BASIC_AUTH);
    }

    if (data.user) {
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(data.user));
      inMemoryUser = data.user;
    } else if (data.user === null) {
      localStorage.removeItem(STORAGE_KEY_USER);
      inMemoryUser = null;
    }
  } catch (e) {
    console.error("Failed to save auth credentials:", e);
  }

  broadcastAuthState(inMemoryUser);
}

/**
 * 退出登录，清理凭据
 */
export function logout() {
  try {
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_BASIC_AUTH);
    localStorage.removeItem(STORAGE_KEY_USER);
  } catch {
    // ignore
  }
  inMemoryUser = null;
  broadcastAuthState(null);
}

/**
 * 乐观更新当前用户的已用空间 (上传成功后调用)
 */
export function updateOptimisticQuota(addedBytes: number) {
  const current = getCurrentUser();
  if (!current) return;
  const updated: CurrentUser = {
    ...current,
    usedBytes: Math.max(0, (current.usedBytes || 0) + addedBytes),
  };
  saveAuthCredentials({ user: updated });
}

/**
 * 统一登录逻辑
 * 依次尝试：
 * 1. /api/center/login (可取得专用 Token 及完整信息)
 * 2. /api/me (读取当前账号信息)
 * 3. WebDAV PROPFIND /webdav/ (凭据验证与 RFC 4331 配额解析)
 */
export async function loginWithCredentials(
  username: string,
  password: string
): Promise<CurrentUser> {
  const cleanUsername = username.trim();
  const basicAuth = `Basic ${btoa(unescape(encodeURIComponent(`${cleanUsername}:${password}`)))}`;

  // 1. 优先尝试控制中心登录接口
  try {
    const centerRes = await fetch("/api/center/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: basicAuth,
      },
      body: JSON.stringify({ username: cleanUsername, password }),
    });

    if (centerRes.ok) {
      const data: any = await centerRes.json();
      if (data && data.success) {
        const loggedUser: CurrentUser = {
          id: data.user?.id || cleanUsername,
          username: data.user?.username || cleanUsername,
          role: data.user?.role || "admin",
          enabled: data.user?.enabled !== false,
          rootPath: data.user?.rootPath ?? "",
          permission: data.user?.permission || "rw",
          quotaBytes: Number(data.user?.quotaBytes || 0),
          usedBytes: Number(data.user?.usedBytes || 0),
        };

        saveAuthCredentials({
          token: data.token || null,
          basicAuth,
          user: loggedUser,
        });

        return loggedUser;
      }
    }
  } catch (e) {
    console.warn("POST /api/center/login failed or not available:", e);
  }

  // 2. 尝试 /api/me
  try {
    const meRes = await fetch("/api/me", {
      headers: {
        Authorization: basicAuth,
      },
    });

    if (meRes.ok) {
      const meData: any = await meRes.json();
      const loggedUser: CurrentUser = {
        id: meData.id || cleanUsername,
        username: meData.username || cleanUsername,
        role: meData.role || "user",
        enabled: meData.enabled !== false,
        rootPath: meData.rootPath ?? `users/${cleanUsername}/`,
        permission: meData.permission || "rw",
        quotaBytes: Number(meData.quotaBytes || 0),
        usedBytes: Number(meData.usedBytes || 0),
      };

      saveAuthCredentials({
        token: meData.token || null,
        basicAuth,
        user: loggedUser,
      });

      return loggedUser;
    }
  } catch (e) {
    console.warn("GET /api/me failed or not available:", e);
  }

  // 3. 兜底尝试通过 WebDAV PROPFIND /webdav/ 校验密码有效性
  try {
    const davRes = await fetch("/webdav/", {
      method: "PROPFIND",
      headers: {
        Depth: "0",
        Authorization: basicAuth,
      },
    });

    if (davRes.status === 401) {
      throw new Error("用户名或密码错误");
    }

    if (davRes.ok || davRes.status === 207) {
      // 成功连通 WebDAV，解析配额 XML
      let quotaBytes = 0;
      let usedBytes = 0;

      try {
        const xmlText = await davRes.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlText, "application/xml");
        const quotaAvailableNode = doc.querySelector("quota-available-bytes");
        const quotaUsedNode = doc.querySelector("quota-used-bytes");

        if (quotaUsedNode && quotaUsedNode.textContent) {
          usedBytes = Number(quotaUsedNode.textContent) || 0;
        }
        if (quotaAvailableNode && quotaAvailableNode.textContent) {
          const available = Number(quotaAvailableNode.textContent) || 0;
          quotaBytes = usedBytes + available;
        }
      } catch {
        // ignore xml parse error
      }

      // 如果用户名是 admin，默认为管理员；否则为普通用户
      const isLikelyAdmin = cleanUsername.toLowerCase() === "admin";
      const loggedUser: CurrentUser = {
        id: cleanUsername,
        username: cleanUsername,
        role: isLikelyAdmin ? "admin" : "user",
        enabled: true,
        rootPath: isLikelyAdmin ? "" : `users/${cleanUsername}/`,
        permission: "rw",
        quotaBytes,
        usedBytes,
      };

      saveAuthCredentials({
        basicAuth,
        user: loggedUser,
      });

      return loggedUser;
    }
  } catch (davErr: any) {
    if (davErr.message === "用户名或密码错误") throw davErr;
    console.warn("WebDAV PROPFIND verification error:", davErr);
  }

  throw new Error("登录验证失败，请检查账号密码或网络连接");
}

/**
 * 刷新当前用户资料与配额
 */
export async function refreshCurrentUserProfile(): Promise<CurrentUser | null> {
  const current = getCurrentUser();
  const basicAuth = getStoredBasicAuth();
  const token = getStoredToken();

  if (!basicAuth && !token && !current) {
    return null;
  }

  const headers = getAuthHeaders();

  // 1. 尝试 /api/me
  try {
    const res = await fetch("/api/me", { headers });
    if (res.ok) {
      const data: any = await res.json();
      const updated: CurrentUser = {
        id: data.id || current?.id || data.username,
        username: data.username || current?.username || "user",
        role: data.role || current?.role || "user",
        enabled: data.enabled !== false,
        rootPath: data.rootPath ?? (current?.rootPath || ""),
        permission: data.permission || current?.permission || "rw",
        quotaBytes: Number(data.quotaBytes ?? current?.quotaBytes ?? 0),
        usedBytes: Number(data.usedBytes ?? current?.usedBytes ?? 0),
      };
      saveAuthCredentials({ user: updated });
      return updated;
    }
  } catch {
    // ignore
  }

  // 2. 尝试从 WebDAV PROPFIND 读取 RFC 4331 配额
  try {
    const davRes = await fetch("/webdav/", {
      method: "PROPFIND",
      headers: {
        Depth: "0",
        ...headers,
      },
    });

    if (davRes.ok || davRes.status === 207) {
      const xmlText = await davRes.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlText, "application/xml");
      const quotaAvailableNode = doc.querySelector("quota-available-bytes");
      const quotaUsedNode = doc.querySelector("quota-used-bytes");

      if (quotaUsedNode || quotaAvailableNode) {
        let usedBytes = Number(quotaUsedNode?.textContent) || (current?.usedBytes || 0);
        let quotaBytes = current?.quotaBytes || 0;
        if (quotaAvailableNode?.textContent) {
          quotaBytes = usedBytes + (Number(quotaAvailableNode.textContent) || 0);
        }

        const updated: CurrentUser = {
          ...(current || {
            username: parseUsernameFromBasicAuth(basicAuth || "") || "user",
            role: "user",
            enabled: true,
            rootPath: "",
            permission: "rw",
          }),
          quotaBytes,
          usedBytes,
        };
        saveAuthCredentials({ user: updated });
        return updated;
      }
    }
  } catch {
    // ignore
  }

  return current;
}

/**
 * 专供控制中心登录: 验证权限并获取专用 Token (支持 Turnstile 人机验证)
 */
export async function loginAsAdmin(
  username: string,
  password: string,
  turnstileToken?: string
): Promise<CurrentUser> {
  const cleanUsername = username.trim();
  const basicAuth = `Basic ${btoa(unescape(encodeURIComponent(`${cleanUsername}:${password}`)))}`;
  const res = await fetch("/api/center/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: basicAuth,
    },
    body: JSON.stringify({
      username: cleanUsername,
      password,
      turnstileToken: turnstileToken || undefined,
    }),
  });

  if (!res.ok) {
    const errData: any = await res.json().catch(() => null);
    if (errData?.error) {
      throw new Error(errData.error);
    }
    if (res.status === 401 || res.status === 403) {
      throw new Error("用户名或密码错误");
    }
    throw new Error(`登录失败: HTTP ${res.status}`);
  }

  const data: any = await res.json();
  if (!data?.success || data.user?.role !== "admin") {
    throw new Error("账号权限不足，无法访问控制中心");
  }

  const loggedUser: CurrentUser = {
    id: data.user.id || cleanUsername,
    username: data.user.username || cleanUsername,
    role: "admin",
    enabled: data.user.enabled !== false,
    rootPath: data.user.rootPath ?? "",
    permission: data.user.permission || "rw",
    quotaBytes: Number(data.user.quotaBytes || 0),
    usedBytes: Number(data.user.usedBytes || 0),
  };

  saveAuthCredentials({
    token: data.token || null,
    basicAuth,
    user: loggedUser,
  });

  return loggedUser;
}

/**
 * 检查当前是否有管理员权限已登录
 */
export function isAdminLoggedIn(): boolean {
  const user = getCurrentUser();
  const token = getStoredToken();
  return Boolean(user && user.role === "admin" && user.enabled && token);
}
